import React, { useEffect, useState } from "react";
import { Box, Link, Typography } from "@mui/material";

// Inventory is bought and managed on AHA's own eCards site; the platform only reads balances.
export const AHA_ECARDS_URL = "https://ecards.heart.org/";

const MUTED = "var(--color-accent-violet-mid, var(--color-ink-3))";
const LINE = "var(--color-hairline-cloud)";

const hasBalance = (product) => product.availableBalance != null;
const timeLabel = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : null);

/** Per product, how many cards a refresh added compared with the figures shown before it. */
function balanceDeltas(before, after) {
  const previous = new Map((before?.products || []).filter(hasBalance).map((p) => [p.productKey, p.availableBalance]));
  return new Map((after?.products || []).filter(hasBalance)
    .filter((p) => previous.has(p.productKey))
    .map((p) => [p.productKey, p.availableBalance - previous.get(p.productKey)]));
}

function RefreshIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><path d="M21 3v5h-5" />
    </svg>
  );
}

function RefreshButton({ refreshing, onClick }) {
  return (
    <Box component="button" type="button" onClick={onClick} disabled={refreshing}
      sx={{
        ml: "auto", flexShrink: 0, display: "inline-flex", alignItems: "center", gap: "6px", height: 34, px: "12px",
        border: `1px solid ${LINE}`, borderRadius: "8px", bgcolor: "var(--color-surface-canvas-light, #fff)",
        font: "700 11px var(--font-ui)", letterSpacing: ".06em", textTransform: "uppercase",
        color: "var(--color-ink)", cursor: "pointer", transition: "background .15s",
        "&:hover": { bgcolor: "var(--color-surface-press-light)" },
        "&:disabled": { color: MUTED, cursor: "default" },
        "& svg": refreshing ? { animation: "aha-inv-spin .8s linear infinite" } : undefined,
        "@keyframes aha-inv-spin": { to: { transform: "rotate(360deg)" } },
      }}>
      <RefreshIcon />
      <span>{refreshing ? "Pulling…" : "Refresh"}</span>
    </Box>
  );
}

function ProductCard({ product, delta }) {
  return (
    <Box data-testid={`aha-inventory-${product.productKey}`}
      sx={{ border: `1px solid ${LINE}`, borderRadius: "10px", px: 2, py: 1.75, display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto", columnGap: 1.5, rowGap: 0.5, alignItems: "center" }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: "var(--font-ui)", fontSize: 14, fontWeight: 700, lineHeight: 1.35 }}>
          {product.productName || product.productKey}
        </Typography>
        {product.productNumber && (
          <Typography sx={{ fontFamily: "var(--font-ui)", fontSize: 12, color: MUTED }}>
            Product <Box component="span" sx={{ fontFamily: "var(--font-mono)" }}>{product.productNumber}</Box>
          </Typography>
        )}
      </Box>
      {hasBalance(product) ? (
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontFamily: "var(--font-mono)", fontSize: 18, fontWeight: 600, lineHeight: 1.2 }}>
            {Number(product.availableBalance).toLocaleString()}
          </Typography>
          <Typography component="div" sx={{ fontFamily: "var(--font-ui)", fontSize: 11, color: MUTED }}>
            {delta > 0 && (
              <Box component="span" sx={{ fontWeight: 700, color: "var(--color-success)", mr: "4px" }}>
                {`+${delta.toLocaleString()}`}
              </Box>
            )}
            <span>cards left</span>
          </Typography>
        </Box>
      ) : (
        <Typography sx={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--color-warn, #a86200)", textAlign: "right", maxWidth: 160 }}>
          {product.error || "Balance unavailable"}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Coordinator dashboard: remaining AHA eCard inventory per product, with a Refresh that asks AHA again.
 * model.inventory is the /api/v1/aha/inventory answer; model.refresh() resolves to a fresh one.
 */
export default function AhaInventoryWidget({ model = {} }) {
  const [inventory, setInventory] = useState(model.inventory || null);
  const [deltas, setDeltas] = useState(new Map());
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState("");

  useEffect(() => { setInventory(model.inventory || null); }, [model.inventory]);

  const products = inventory?.products || [];
  const counted = products.filter(hasBalance);
  const total = counted.reduce((sum, p) => sum + Number(p.availableBalance), 0);
  const pulledAt = timeLabel(inventory?.observedAt);

  const handleRefresh = async () => {
    if (refreshing || !model.refresh) return;
    setRefreshing(true);
    setRefreshError("");
    try {
      const fresh = await model.refresh();
      setDeltas(balanceDeltas(inventory, fresh));
      setInventory(fresh);
    } catch {
      setRefreshError("Could not reach AHA. Showing the last figures.");
    } finally {
      setRefreshing(false);
    }
  };

  if (!products.length) {
    return (
      <Typography sx={{ fontFamily: "var(--font-ui)", fontSize: 13, color: MUTED }}>
        {inventory?.error || "No AHA products are configured on this center's classes."}
      </Typography>
    );
  }

  return (
    <Box sx={{ fontFamily: "var(--font-ui)" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
        <Typography data-testid="aha-inventory-total" component="div"
          sx={{ fontFamily: "var(--font-display)", fontSize: 30, fontWeight: 700, lineHeight: 1.1 }}>
          <span>{total.toLocaleString()}</span>
          <Box component="small" sx={{ fontFamily: "var(--font-ui)", fontSize: 13, fontWeight: 500, color: MUTED, ml: "6px" }}>
            {`cards across ${counted.length} course${counted.length === 1 ? "" : "s"}`}
          </Box>
        </Typography>
        <RefreshButton refreshing={refreshing} onClick={handleRefresh} />
      </Box>

      <Box sx={{ display: "grid", gap: 1.25 }}>
        {products.map((product) => (
          <ProductCard key={product.productKey} product={product} delta={deltas.get(product.productKey)} />
        ))}
      </Box>

      {refreshError && (
        <Typography role="status" sx={{ mt: 1.5, fontSize: 12, color: "var(--color-warn, #a86200)" }}>{refreshError}</Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", mt: 2, fontSize: 12, color: MUTED }}>
        <span>{pulledAt ? `Last pulled from AHA at ${pulledAt}` : "Not yet pulled from AHA"}</span>
        {model.onViewIssued && (
          <Link component="button" type="button" onClick={model.onViewIssued}
            sx={{ fontSize: 12, fontWeight: 600, color: "var(--color-accent-violet)" }}>
            View issued cards
          </Link>
        )}
        <Link href={AHA_ECARDS_URL} target="_blank" rel="noopener noreferrer"
          sx={{ fontSize: 12, fontWeight: 600, color: "var(--color-accent-violet)", "&:hover": { color: "var(--color-accent-violet-deep)" } }}>
          Manage inventory
        </Link>
      </Box>
    </Box>
  );
}
